import { useEffect, useRef, useState } from "react";

/* Shanso Ma'am — animated SVG teacher with audio-reactive lip sync.
   moods: idle | thinking | talking | celebrating */
export const TeacherAvatar = ({ mood = "idle", audioObj, size = 96 }) => {
  const [mouth, setMouth] = useState(0);
  const rafRef = useRef(null);

  useEffect(() => {
    if (!audioObj) return;
    let stopped = false;
    let fakeTimer = null;
    let ctx = null;
    const stopAll = () => {
      stopped = true;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (fakeTimer) clearInterval(fakeTimer);
      setMouth(0);
      if (ctx) { try { ctx.close(); } catch {} ctx = null; }
    };
    const startFake = () => {
      fakeTimer = setInterval(() => {
        if (audioObj.paused || audioObj.ended) { stopAll(); return; }
        setMouth(0.25 + Math.random() * 0.55);
      }, 110);
    };
    const start = async () => {
      try {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        ctx = new Ctx();
        try { await ctx.resume(); } catch {}
        if (ctx.state !== "running") { try { ctx.close(); } catch {} ctx = null; startFake(); return; }
        const src = ctx.createMediaElementSource(audioObj);
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 128;
        src.connect(analyser);
        analyser.connect(ctx.destination);
        const data = new Uint8Array(analyser.frequencyBinCount);
        const tick = () => {
          if (stopped) return;
          if (audioObj.paused || audioObj.ended) { stopAll(); return; }
          analyser.getByteFrequencyData(data);
          let sum = 0;
          for (let i = 1; i < 32; i++) sum += data[i];
          setMouth(Math.min(1, (sum / 31 / 255) * 2.4));
          rafRef.current = requestAnimationFrame(tick);
        };
        tick();
      } catch { startFake(); }
    };
    start();
    return stopAll;
  }, [audioObj]);

  const talking = mood === "talking";
  const thinking = mood === "thinking";
  const celebrating = mood === "celebrating";
  const speaking = (talking || celebrating) && mouth > 0.04;
  const mouthRy = speaking ? 1.5 + mouth * 7 : 0;
  const mouthRx = speaking ? 6.5 + mouth * 3 : 0;

  return (
    <div className="teacher-avatar select-none" data-testid="teacher-avatar" data-mood={mood} style={{ width: size, height: size * 1.15 }}>
      <style>{`
        .teacher-avatar .ta-lids { transform-origin: center; animation: ta-blink 4.2s infinite; }
        @keyframes ta-blink { 0%,94%,100% { transform: scaleY(0); } 96%,98% { transform: scaleY(1); } }
        .teacher-avatar[data-mood="talking"] .ta-head { animation: ta-bob 1.1s ease-in-out infinite; }
        @keyframes ta-bob { 0%,100% { transform: translateY(0); } 50% { transform: translateY(1.6px); } }
        .teacher-avatar[data-mood="celebrating"] .ta-arm-r { animation: ta-wave 0.55s ease-in-out infinite alternate; }
        .teacher-avatar .ta-arm-r { transform-origin: 138px 148px; transition: transform 0.4s; }
        @keyframes ta-wave { from { transform: rotate(-118deg); } to { transform: rotate(-152deg); } }
        .teacher-avatar .ta-dot { opacity: 0; }
        .teacher-avatar[data-mood="thinking"] .ta-dot { animation: ta-dots 1.2s infinite; }
        .teacher-avatar[data-mood="thinking"] .ta-dot:nth-child(2) { animation-delay: 0.2s; }
        .teacher-avatar[data-mood="thinking"] .ta-dot:nth-child(3) { animation-delay: 0.4s; }
        @keyframes ta-dots { 0%,100% { opacity: 0.15; } 50% { opacity: 1; } }
        .teacher-avatar .ta-star { opacity: 0; }
        .teacher-avatar[data-mood="celebrating"] .ta-star { animation: ta-pop 0.9s ease-out infinite; }
        .teacher-avatar[data-mood="celebrating"] .ta-star:nth-of-type(2) { animation-delay: 0.25s; }
        .teacher-avatar[data-mood="celebrating"] .ta-star:nth-of-type(3) { animation-delay: 0.5s; }
        @keyframes ta-pop { 0% { opacity: 0; transform: scale(0.4); } 40% { opacity: 1; transform: scale(1.15); } 100% { opacity: 0; transform: scale(0.8) translateY(-8px); } }
        .teacher-avatar[data-mood="thinking"] .ta-brows { transform: translateY(-2.5px); }
        .teacher-avatar .ta-brows { transition: transform 0.3s; }
      `}</style>
      <svg viewBox="0 0 200 230" width="100%" height="100%">
        {/* thinking dots */}
        <g fill="#67e8f9">
          <circle className="ta-dot" cx="150" cy="34" r="3.5"/>
          <circle className="ta-dot" cx="162" cy="24" r="4.5"/>
          <circle className="ta-dot" cx="176" cy="12" r="5.5"/>
        </g>
        {/* celebration stars */}
        <g>
          <path className="ta-star" d="M38 30 l3 7 7 1 -5 5 1.5 7 -6.5-3.5 -6.5 3.5 1.5-7 -5-5 7-1z" fill="#fbbf24"/>
          <path className="ta-star" d="M166 52 l2.5 6 6 1 -4.5 4.5 1 6 -5-3 -5 3 1-6 -4.5-4.5 6-1z" fill="#22d3ee"/>
          <path className="ta-star" d="M28 82 l2 5 5 0.5 -3.5 4 1 5 -4.5-2.5 -4.5 2.5 1-5 -3.5-4 5-0.5z" fill="#a78bfa"/>
        </g>

        {/* torso — teal kurti */}
        <path d="M62 228 Q60 172 78 158 Q88 150 100 150 Q112 150 122 158 Q140 172 138 228 Z" fill="#0e7490" stroke="#164e63" strokeWidth="2"/>
        <path d="M78 158 Q100 176 122 158 L122 168 Q100 188 78 168 Z" fill="#22d3ee" opacity="0.35"/>
        {/* left arm holding book */}
        <path d="M70 164 Q52 182 56 206" stroke="#0e7490" strokeWidth="13" fill="none" strokeLinecap="round"/>
        <rect x="42" y="198" width="30" height="22" rx="3" fill="#7c3aed" stroke="#4c1d95" strokeWidth="2"/>
        <line x1="57" y1="198" x2="57" y2="220" stroke="#ddd6fe" strokeWidth="2"/>
        {/* right arm — waves when celebrating */}
        <g className="ta-arm-r">
          <path d="M132 158 Q152 168 158 188" stroke="#0e7490" strokeWidth="13" fill="none" strokeLinecap="round"/>
          <circle cx="160" cy="192" r="8" fill="#f3c29e"/>
        </g>

        {/* head group */}
        <g className="ta-head">
          {/* hair back + bun */}
          <circle cx="100" cy="42" r="15" fill="#3b2417"/>
          <circle cx="100" cy="88" r="47" fill="#3b2417"/>
          {/* face */}
          <ellipse cx="100" cy="92" rx="36" ry="38" fill="#f3c29e"/>
          {/* hairline */}
          <path d="M64 86 Q66 52 100 50 Q134 52 136 86 Q126 66 100 64 Q74 66 64 86 Z" fill="#3b2417"/>
          {/* ears + earrings */}
          <circle cx="63" cy="94" r="6" fill="#f3c29e"/>
          <circle cx="137" cy="94" r="6" fill="#f3c29e"/>
          <circle cx="63" cy="102" r="2.5" fill="#fbbf24"/>
          <circle cx="137" cy="102" r="2.5" fill="#fbbf24"/>
          {/* bindi */}
          <circle cx="100" cy="74" r="2.6" fill="#dc2626"/>
          {/* eyebrows */}
          <g className="ta-brows" stroke="#2b1a12" strokeWidth="2.4" strokeLinecap="round" fill="none">
            <path d="M76 78 Q84 74 92 78"/>
            <path d="M108 78 Q116 74 124 78"/>
          </g>
          {/* eyes */}
          <g>
            <ellipse cx="84" cy="88" rx="6" ry="6.5" fill="#fff"/>
            <ellipse cx="116" cy="88" rx="6" ry="6.5" fill="#fff"/>
            <circle cx={thinking ? 85.5 : 84} cy={thinking ? 86 : 88.5} r="2.8" fill="#2b1a12"/>
            <circle cx={thinking ? 117.5 : 116} cy={thinking ? 86 : 88.5} r="2.8" fill="#2b1a12"/>
            <rect className="ta-lids" x="77" y="82" width="14" height="12" rx="6" fill="#f3c29e"/>
            <rect className="ta-lids" x="109" y="82" width="14" height="12" rx="6" fill="#f3c29e"/>
          </g>
          {/* glasses */}
          <g stroke="#22d3ee" strokeWidth="2" fill="none" opacity="0.9">
            <rect x="75" y="80" width="19" height="15" rx="7"/>
            <rect x="107" y="80" width="19" height="15" rx="7"/>
            <line x1="94" y1="87" x2="107" y2="87"/>
          </g>
          {/* nose */}
          <path d="M99 94 Q97 102 100 103" stroke="#d69a6f" strokeWidth="2" fill="none" strokeLinecap="round"/>
          {/* mouth: open ellipse while talking, smile otherwise */}
          {speaking ? (
            <g>
              <ellipse cx="100" cy="113" rx={mouthRx} ry={mouthRy} fill="#7f1d1d"/>
              <ellipse cx="100" cy={113 + mouthRy * 0.45} rx={mouthRx * 0.55} ry={Math.max(mouthRy * 0.4, 0.6)} fill="#ef8080"/>
            </g>
          ) : (
            <path d={celebrating ? "M88 110 Q100 122 112 110" : "M90 111 Q100 118 110 111"} stroke="#7f1d1d" strokeWidth="2.6" fill="none" strokeLinecap="round"/>
          )}
        </g>
      </svg>
    </div>
  );
};
